import { useState, useEffect, useCallback, useRef } from "react";
import * as Notifications from "expo-notifications";
import { AppState, Platform, type AppStateStatus } from "react-native";
import { useRouter } from "expo-router";
import { supabase } from "@/lib/supabase/client";
import { useAuth } from "@/providers/AuthProvider";
import {
  registerForPushNotifications,
  savePushToken,
  markNotificationAsRead,
  getUnreadNotificationCount,
  clearDeviceNotifications,
  getNotificationPermissionStatus,
  requestNotificationPermission,
  openNotificationSettings,
  wasPromptRecentlyDismissed,
  dismissNotificationPrompt,
  type NotificationPermissionStatus,
} from "./index";

export interface InAppNotification {
  id: string;
  user_id: string;
  title: string;
  body: string;
  type: string;
  notification_type?: string;
  action_url?: string;
  action_data?: Record<string, unknown>;
  is_read: boolean;
  read_at?: string;
  sender_id?: string;
  weekend_id?: string;
  event_id?: string;
  lottie_animation?: string;
  created_at: string;
}

interface UseNotificationsReturn {
  notifications: InAppNotification[];
  unreadCount: number;
  isLoading: boolean;
  error: string | null;
  pushToken: string | null;
  /** OS-level permission status */
  permissionStatus: NotificationPermissionStatus;
  /** Whether to show the soft prompt banner */
  showPermissionPrompt: boolean;
  /** User taps "Aanzetten" — requests OS permission */
  handleEnableNotifications: () => Promise<void>;
  /** User taps "Open Instellingen" */
  handleOpenSettings: () => void;
  /** User dismisses the prompt banner */
  handleDismissPrompt: () => Promise<void>;
  refetch: () => Promise<void>;
  markAsRead: (notificationId: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
}

const MAX_RETRY_ATTEMPTS = 3;

/**
 * Hook for managing notifications with foreground refresh and permission prompts.
 *
 * Uses activeProfile to support guardian mode — when a guardian switches to
 * a dependent profile, notifications for that dependent are shown.
 */
export const useNotifications = (): UseNotificationsReturn => {
  const { user, activeProfile } = useAuth();
  const router = useRouter();

  const [notifications, setNotifications] = useState<InAppNotification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pushToken, setPushToken] = useState<string | null>(null);
  const [permissionStatus, setPermissionStatus] = useState<NotificationPermissionStatus>("undetermined");
  const [showPermissionPrompt, setShowPermissionPrompt] = useState(false);

  const notificationListener = useRef<Notifications.Subscription | null>(null);
  const responseListener = useRef<Notifications.Subscription | null>(null);
  const appStateRef = useRef<AppStateStatus>(AppState.currentState);

  const targetUserId = activeProfile?.id;

  /**
   * Register push token with retry logic
   */
  const registerAndSaveToken = useCallback(async (attempt = 1): Promise<void> => {
    if (!user?.id) return;

    const token = await registerForPushNotifications();
    if (token) {
      setPushToken(token);
      const saved = await savePushToken(user.id, token);
      if (!saved && attempt < MAX_RETRY_ATTEMPTS) {
        const delay = Math.pow(2, attempt) * 1000;
        setTimeout(() => registerAndSaveToken(attempt + 1), delay);
      }
    }
  }, [user?.id]);

  /**
   * Check permission status and decide whether to show prompt
   */
  const checkPermissionAndRegister = useCallback(async (): Promise<void> => {
    const status = await getNotificationPermissionStatus();
    setPermissionStatus(status);

    if (status === "granted") {
      setShowPermissionPrompt(false);
      await registerAndSaveToken();
      return;
    }

    if (status === "undetermined" || (status === "denied" && Platform.OS === "android")) {
      await new Promise((resolve) => setTimeout(resolve, 500));
      const granted = await requestNotificationPermission();
      if (granted) {
        setPermissionStatus("granted");
        setShowPermissionPrompt(false);
        await registerAndSaveToken();
        return;
      } else {
        setPermissionStatus("denied");
      }
    }

    const dismissed = await wasPromptRecentlyDismissed();
    setShowPermissionPrompt(!dismissed);
  }, [registerAndSaveToken]);

  /**
   * User taps "Aanzetten" on the soft prompt
   */
  const handleEnableNotifications = useCallback(async (): Promise<void> => {
    const granted = await requestNotificationPermission();
    if (granted) {
      setPermissionStatus("granted");
      setShowPermissionPrompt(false);
      await registerAndSaveToken();
    } else {
      setPermissionStatus("denied");
      await dismissNotificationPrompt();
      setShowPermissionPrompt(false);
    }
  }, [registerAndSaveToken]);

  const handleOpenSettings = useCallback((): void => {
    openNotificationSettings();
  }, []);

  const handleDismissPrompt = useCallback(async (): Promise<void> => {
    await dismissNotificationPrompt();
    setShowPermissionPrompt(false);
  }, []);

  /**
   * Fetch notifications from database
   */
  const fetchNotifications = useCallback(async () => {
    if (!targetUserId) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const { data, error: fetchError } = await supabase
        .from("notifications")
        .select("*")
        .eq("user_id", targetUserId)
        .order("created_at", { ascending: false })
        .limit(50);

      if (fetchError) throw fetchError;

      setNotifications(data || []);

      const count = await getUnreadNotificationCount(targetUserId);
      setUnreadCount(count);
    } catch (err: any) {
      console.error("Error fetching notifications:", err);
      setError(err.message || "Could not load notifications");
    } finally {
      setIsLoading(false);
    }
  }, [targetUserId]);

  /**
   * Check permission + register on mount and on every foreground transition
   */
  useEffect(() => {
    if (!user?.id) return;

    checkPermissionAndRegister();

    const handleAppStateChange = (nextState: AppStateStatus) => {
      if (appStateRef.current.match(/inactive|background/) && nextState === "active") {
        checkPermissionAndRegister();
      }
      appStateRef.current = nextState;
    };

    const subscription = AppState.addEventListener("change", handleAppStateChange);
    return () => subscription.remove();
  }, [user?.id, checkPermissionAndRegister]);

  /**
   * Set up notification listeners
   */
  useEffect(() => {
    notificationListener.current = Notifications.addNotificationReceivedListener(() => {
      fetchNotifications();
    });

    responseListener.current = Notifications.addNotificationResponseReceivedListener(async (response) => {
      const data = response.notification.request.content.data;

      if (targetUserId) {
        try {
          const { error: markError } = await supabase
            .from("notifications")
            .update({
              is_read: true,
              read_at: new Date().toISOString(),
            })
            .eq("user_id", targetUserId)
            .eq("is_read", false);

          if (!markError) {
            setNotifications((prev) =>
              prev.map((n) => ({ ...n, is_read: true, read_at: new Date().toISOString() }))
            );
            setUnreadCount(0);
          }
        } catch (err) {
          console.error("Error marking all as read on tap:", err);
        }
      }
      await clearDeviceNotifications();

      if (data.type === "chat_message" && data.chatId) {
        router.push(`/chat-screens/${data.chatId}` as any);
      } else if (data.type === "event_reminder" && data.eventId) {
        router.push(`/agenda/${data.eventId}` as `/agenda/${string}`);
      } else if (data.url) {
        router.push(data.url as any);
      } else if (data.actionUrl) {
        router.push(data.actionUrl as `/agenda/${string}`);
      }
    });

    return () => {
      notificationListener.current?.remove();
      responseListener.current?.remove();
    };
  }, [fetchNotifications, router, targetUserId]);

  /**
   * Subscribe to real-time notification updates for the active profile
   */
  useEffect(() => {
    if (!targetUserId) return;

    const channel = supabase
      .channel(`notifications_${targetUserId}`)
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "notifications",
          filter: `user_id=eq.${targetUserId}`,
        },
        (payload) => {
          setNotifications((prev) => [payload.new as InAppNotification, ...prev]);
          setUnreadCount((prev) => prev + 1);
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [targetUserId]);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  const markAsRead = useCallback(
    async (notificationId: string) => {
      const success = await markNotificationAsRead(notificationId);
      if (success) {
        setNotifications((prev) =>
          prev.map((n) =>
            n.id === notificationId
              ? { ...n, is_read: true, read_at: new Date().toISOString() }
              : n
          )
        );
        setUnreadCount((prev) => Math.max(0, prev - 1));
      }
    },
    []
  );

  const markAllAsRead = useCallback(async () => {
    if (!targetUserId) return;

    try {
      const { error } = await supabase
        .from("notifications")
        .update({
          is_read: true,
          read_at: new Date().toISOString(),
        })
        .eq("user_id", targetUserId)
        .eq("is_read", false);

      if (error) throw error;

      setNotifications((prev) =>
        prev.map((n) => ({ ...n, is_read: true, read_at: new Date().toISOString() }))
      );
      setUnreadCount(0);
    } catch (err) {
      console.error("Error marking all as read:", err);
    }
  }, [targetUserId]);

  return {
    notifications,
    unreadCount,
    isLoading,
    error,
    pushToken,
    permissionStatus,
    showPermissionPrompt,
    handleEnableNotifications,
    handleOpenSettings,
    handleDismissPrompt,
    refetch: fetchNotifications,
    markAsRead,
    markAllAsRead,
  };
};

export default useNotifications;

